// Mermaid diagram renderer — RFC-0010 §4 (PD-0011 S2 render half).
//
// The strict security posture and version pin live in ./mermaid-init; this file
// is the render component only. See mermaid-init.ts for the CSP (N4) analysis.

import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { initMermaid } from './mermaid-init';

let diagramSeq = 0;

interface MermaidDiagramProps {
  chart: string;
}

/**
 * Renders a fenced ```mermaid block. On a parse/render error it falls back to
 * an inline error note plus the raw diagram source (never a blank block).
 */
export function MermaidDiagram({ chart }: MermaidDiagramProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef(`mermaid-${(diagramSeq += 1)}`);

  useEffect(() => {
    let cancelled = false;
    initMermaid();

    mermaid
      .render(idRef.current, chart)
      .then((result) => {
        if (cancelled) return;
        // Mermaid strict mode has already sanitized this SVG (DOMPurify); it is
        // trusted output generated from the diagram source, injected post-
        // sanitize per AD-0001 §14.1. Intentionally NOT calling
        // result.bindFunctions — strict mode emits none, and we bind none.
        setSvg(result.svg);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setSvg(null);
      });

    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (error !== null) {
    return (
      <div className="my-4 overflow-x-auto rounded-lg border border-error-border bg-error-bg/40 p-4">
        <p className="mb-2 text-[12px] font-semibold text-badge-error-text">
          Could not render diagram
        </p>
        <pre className="whitespace-pre-wrap text-[12px] text-text-2">
          <code>{chart}</code>
        </pre>
      </div>
    );
  }

  if (svg !== null) {
    return (
      <div
        className="mermaid-diagram my-4 flex justify-center overflow-x-auto"
        // SVG is Mermaid strict-mode (DOMPurify-sanitized) output injected
        // post-sanitize per AD-0001 §14.1 — see mermaid-init.ts.
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }

  return (
    <div className="my-4 rounded-lg border border-border bg-surface p-4 text-[12px] text-muted">
      Rendering diagram…
    </div>
  );
}
